import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import { Row, Col, Alert, Form, FormGroup, Label, Input, InputGroup, InputGroupAddon, InputGroupText, Select, ButtonGroup, Button, FormText } from 'reactstrap';
import FontAwesome from 'react-fontawesome';

import Autocomplete from 'react-autocomplete';

import { editAddressRequest } from '../../AddressActions';
import { editUserRequest } from '../../../User/UserActions';
import { getLanguagePath } from '../../../Intl/IntlActions';
import { getAvalaibleCountries, getCountryNameTranslation } from '../../../App/AppActions';
import { displayErrors } from '../../../Error/ErrorActions';

class AddressEditForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isInitiated: false,
            address: props.address ? props.address : {},
            isSaving: false,
        };

        this.countries = getAvalaibleCountries();

        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.closeForm = this.closeForm.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if(!this.state.isInitiated && !this.state.address.name && this.props.user && (this.props.user.firstName || this.props.user.lastName)) {
            this.init();
        }
    }

    getCountryData(countryName = null) {
        countryName = `${countryName || this.state.address.country}`.toLowerCase().trim();
        let countryData = this.countries.find(country => country.cca2.toLowerCase().trim() === countryName);
        if(!countryData) {
            countryData = this.countries.find(country => (
                country.name.common.toLowerCase().trim() === countryName
                || Object.entries(country.translations).some(translation => ((translation[1] || {}).common || '').toLowerCase() === countryName)
            ));
            if(!countryData) {
                countryData = this.countries.find(country => Object.entries(country.translations).some(translation => (translation[1].common || '').toLowerCase().indexOf(countryName) !== -1));
            }
        }
        return countryData;
    }

    isValid = () => {
        const generalCheck = this.state.address.name && this.state.address.street1 && this.state.address.postalCode && this.state.address.city && this.state.address.country && this.state.address.phone;
        let countryCheck = true;
        const countryData = this.getCountryData();
        switch((countryData || {}).cca2) {
            case 'US':
            case 'CA':
                countryCheck = !!this.state.address.region;
                break;

            default:
        }

        return generalCheck && countryCheck;
    }

    init() {
        this.setState({
            isInitiated: true,
            address: {
                ...this.state.address,
                name: '', // `${this.props.user.firstName || ''} ${this.props.user.lastName || ''}`
            },
        });
    }

    handleChange(event) {
        const { target } = event;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const { name } = target;
        this.setState({
            address: {
                ...this.state.address,
                [name]: value,
            },
        });
    }

    handleSubmit(event) {
        event.preventDefault();

        if(this.isValid()) {
            const countryData = this.getCountryData();

            if(countryData) {
                this.setState({
                    isSaving: true,
                });
                this.props.dispatch(editAddressRequest({
                    ...this.state.address,
                    user: this.props.user && this.props.user._id,
                    label: this.state.address.label || this.state.address.name,
                    countryCode: countryData && countryData.cca2,
                    country: countryData ? countryData.name.common : this.state.country,
                    isUsedForInvoice: this.state.address.isUsedForInvoice || !(this.props.addresses && this.props.addresses.length > 0),
                })).then(address => {
                    this.setState({
                        isSaving: false,
                    });
                    if(address) {
                        this.setState({
                            address,
                        });
                        displayErrors('success', this.props.intl.formatMessage({
                            id: 'addressSavedSuccess',
                            defaultMessage: 'Address saved!',
                        }));
                        // If address name contains user's firstname and user's lastname not already set => Update user's lastname
                        if(!this.props.user.lastName && address.name.includes(this.props.user.firstName)) {
                            this.props.dispatch(editUserRequest({ ...this.props.user, lastName: address.name.replace(this.props.user.firstName, '').trim() }, true));
                        }
                        this.closeForm(address);
                    }
                });
            } else {
                displayErrors('warning', this.props.intl.formatMessage({
                    id: 'alertCountryNotFoundError',
                    defaultMessage: 'Please select a country in the list!',
                }));
            }
        } else {
            displayErrors('error', this.props.intl.formatMessage({
                id: 'alertFormRequiredFieldsError',
                defaultMessage: 'Please fill all required fields!',
            }));
        }
    }

    closeForm(address) {
        if(this.props.closeForm) {
            this.props.closeForm(address);
        } else {
            this.props.history.push(getLanguagePath('/addresses', this.props.intl.locale));
        }
    }

    render() {
        return (
            <Form onSubmit={this.handleSubmit}>
                {
                    this.props.displayLabel
                    ? <FormGroup className="mr-2 mb-2">
                        <Input type="text" name="label" value={this.state.address.label} placeholder={this.props.intl.formatMessage({ id: 'addressLabel', defaultMessage: 'Label (ex: My home)' })} onChange={this.handleChange} />
                      </FormGroup>
                    : null
                }
                <FormGroup className="mr-2 mb-2">
                    <Input type="text" name="name" value={this.state.address.name} placeholder={this.props.intl.formatMessage({ id: 'addressName', defaultMessage: 'First name and last name' })} onChange={this.handleChange} />
                </FormGroup>
                <FormGroup className="mr-2 mb-2">
                    <Input type="text" name="street1" value={this.state.address.street1} placeholder={this.props.intl.formatMessage({ id: 'addressStreet1', defaultMessage: 'Address' })} onChange={this.handleChange} />
                </FormGroup>
                <FormGroup className="mr-2 mb-2">
                    <Input type="text" name="street2" value={this.state.address.street2} placeholder={this.props.intl.formatMessage({ id: 'addressStreet2', defaultMessage: 'Apartment number, digital code, intercom, ...' })} onChange={this.handleChange} />
                </FormGroup>
                <Row noGutters>
                    <Col>
                        <FormGroup className="mr-2 mb-2">
                            <Input type="text" name="postalCode" value={this.state.address.postalCode} placeholder={this.props.intl.formatMessage({ id: 'addressPostalCode', defaultMessage: 'Postal code' })} onChange={this.handleChange} />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup className="mr-2 mb-2">
                            <Input type="text" name="city" value={this.state.address.city} placeholder={this.props.intl.formatMessage({ id: 'addressCity', defaultMessage: 'City' })} onChange={this.handleChange} />
                        </FormGroup>
                    </Col>
                </Row>
                <Row noGutters>
                    <Col>
                        <FormGroup className="mr-2 mb-2">
                            <Input type="text" name="region" value={this.state.address.region} placeholder={this.props.intl.formatMessage({ id: 'addressRegion', defaultMessage: 'Region' })} onChange={this.handleChange} />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup className="mr-2 mb-2">
                            <Autocomplete
                                getItemValue={item => getCountryNameTranslation(item, this.props.intl.locale)}
                                items={this.countries}
                                renderItem={(item, isHighlighted) => (
                                    <div key={item.cca2} className="p-2" style={{ background: isHighlighted ? 'lightgray' : 'white' }}>
                                        {getCountryNameTranslation(item, this.props.intl.locale)}
                                    </div>
                                )}
                                shouldItemRender={(item, value) => getCountryNameTranslation(item, this.props.intl.locale).toLowerCase().indexOf(value.toLowerCase()) > -1}
                                value={this.state.address.country}
                                onChange={event => this.setState({ address: { ...this.state.address, country: event.target.value } })}
                                onSelect={val => this.setState({ address: { ...this.state.address, country: val } })}
                                inputProps={{
                                    className: 'form-control',
                                    placeholder: this.props.intl.formatMessage({ id: 'addressCountry', defaultMessage: 'Country' }),
                                }}
                                wrapperStyle={{}}
                                menuStyle={{
                                    borderRadius: '3px',
                                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
                                    padding: '2px 0',
                                    fontSize: '90%',
                                    position: 'fixed',
                                    overflow: 'auto',
                                    maxHeight: '50%',
                                    zIndex: 999,
                                    background: '#fff',
                                }}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <FormGroup className="mr-2 mb-2">
                    <Input type="text" name="phone" onChange={this.handleChange} placeholder={this.props.intl.formatMessage({ id: 'addressPhone', defaultMessage: 'Phone (useful for delivery)' })} value={this.state.address.phone} />
                </FormGroup>
                <div className="text-right">
                    <ButtonGroup size="sm">
                        <Button color="secondary" outline onClick={event => { event.preventDefault(); this.closeForm(''); }}><FontAwesome name="chevron-left" /> <FormattedMessage id="back" defaultMessage="Back" /></Button>
                        <Button color="warning" disabled={!this.isValid() || this.state.isSaving} onClick={this.handleSubmit}>
                            {
                                this.state.isSaving
                                ? <span><FontAwesome name="spinner" spin /> <FormattedMessage id="saving" defaultMessage="Saving..." /></span>
                                : <span><FontAwesome name="check-circle" /> <FormattedMessage id="save" defaultMessage="Save" /></span>
                            }
                        </Button>
                    </ButtonGroup>
                </div>
            </Form>
        );
    }
}

// <Col>
//     <FormGroup>
//         <Label for="regionField"><FormattedMessage id="addressRegion" defaultMessage="Region" /></Label>
//         <Input type="text" name="region" value={this.state.address.region} onChange={event => this.handleChange(event, true)} />
//     </FormGroup>
// </Col>


function mapStateToProps(store, props) {
    return {
        user: props.user || store.users.user,
        addresses: store.addresses.data,
    };
}

AddressEditForm.defaultProps = {
    user: null,
    addresses: [],
    address: null,
    closeForm: null,
    displayLabel: '',
};

AddressEditForm.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    history: PropTypes.object.isRequired,
    user: PropTypes.object,
    addresses: PropTypes.array,
    address: PropTypes.object,
    displayLabel: PropTypes.bool,
    closeForm: PropTypes.func,
};

export default connect(mapStateToProps)(injectIntl(withRouter(AddressEditForm)));
